import { Dispatch, SetStateAction } from 'react'
import { TFunction } from 'react-i18next'
import api from 'api'
import { ITEMS_PER_PAGE } from 'constant'
import { NotifierType } from 'components/Notifier'
import { Agency } from 'types/agency'
import { Disposition, DispositionCorrection } from 'types/disposition'
import { UpsertCorrectionVariables } from 'api/dispositions/types'
import DownloadUtils from 'utils/download'
import VariableUtils from 'utils/variable'

/** Matches the scale of `disposition_corrections.percent_change`. */
export const PERCENT_CHANGE_DIGITS = 4

/** Matches the scale of the correction prices - accounting invoices them with two decimal places. */
const PRICE_FACTOR = 100

export type AddNotification = (type: NotifierType, text: string | string[], time?: number) => void

/**
 * The backend does not expose the stored file names and the Shrine urls point at opaque S3
 * keys, so the download name is rebuilt here. Global ids are base64 and may contain
 * characters that are not filename safe.
 */
const fileNameFor = (prefix: string, id: string, extension: string): string =>
  `${prefix}_${id.replace(/[^a-zA-Z0-9]/g, '')}.${extension}`

export default class AgencyDispositionsService {
  static getDispositions = (
    agencyId: Agency['id'],
    pageNumber: number,
    setDispositions: Dispatch<SetStateAction<Disposition[]>>,
    setTotalCount: Dispatch<SetStateAction<number>>,
    setLoading: Dispatch<SetStateAction<boolean>>
  ): Promise<void> => {
    setLoading(true)

    return api.dispositions
      .getDispositions({
        agencyId,
        first: pageNumber * ITEMS_PER_PAGE,
        last: ITEMS_PER_PAGE,
      })
      .then(res => {
        const { dispositions } = res.data

        setDispositions(dispositions.nodes)
        setTotalCount(dispositions.totalCount)
      })
      .catch(() => void {})
      .finally(() => setLoading(false))
  }

  /**
   * Mirrors `DispositionCorrection#compute_prices` - a positive percent change raises the price,
   * a negative one lowers it, and the price is rounded to two decimal places.
   */
  static correctedPrice = (price: number, percentChange: number): number =>
    Math.round((price * (1 + percentChange / 100) + Number.EPSILON) * PRICE_FACTOR) / PRICE_FACTOR

  /**
   * Mirrors `DispositionCorrection#percent_change_from_corrected_price` - used when the exact
   * price after the correction is entered instead of the percent.
   */
  static percentChangeFor = (price: number, correctedPrice: number): number =>
    Number(((correctedPrice / price - 1) * 100).toFixed(PERCENT_CHANGE_DIGITS))

  /**
   * Corrections compound - the backend counts every one of them from the price left by the
   * previous correction (`DispositionCorrection#base_price`) and falls back to the disposition
   * price only for the first one. Corrections come sorted by `createdAt` descending, so the
   * previous one is the first entry other than the correction being edited.
   */
  static basePrice = (
    disposition: Disposition,
    correction?: DispositionCorrection | null
  ): number => {
    const previous = disposition.corrections.find(el => el.id !== correction?.id)

    return previous ? previous.correctedPrice : disposition.price
  }

  /** Only one unsent correction per disposition is allowed by the backend. */
  static hasUnsentCorrection = (disposition: Disposition): boolean =>
    disposition.corrections.some(correction => !correction.sentAt)

  static formatPrice = (value: number): string =>
    VariableUtils.formatNumber(value, { style: 'currency', currency: 'PLN' })

  /** The sign carries the direction of the correction, so it is always spelled out. */
  static formatPercent = (percentChange: number): string =>
    `${percentChange > 0 ? '+' : ''}${VariableUtils.formatNumber(percentChange, {
      maximumFractionDigits: PERCENT_CHANGE_DIGITS,
    })}%`

  static unsentCorrections = (disposition: Disposition): DispositionCorrection[] =>
    disposition.corrections.filter(correction => !correction.sentAt)

  static dispositionFileName = (disposition: Disposition): string =>
    fileNameFor('dyspozycja', disposition.id, 'xlsx')

  static invoiceFileName = (disposition: Disposition): string =>
    fileNameFor('faktura', disposition.id, 'pdf')

  static correctionFileName = (correction: DispositionCorrection): string =>
    fileNameFor('korekta', correction.id, 'xlsx')

  static correctionInvoiceFileName = (correction: DispositionCorrection): string =>
    fileNameFor('faktura_korygujaca', correction.id, 'pdf')

  static downloadFile = (fileUrl: string | null, fileName: string): void => {
    if (!fileUrl) {
      return
    }

    void DownloadUtils.downloadFileFromUrl(fileUrl, fileName)
  }

  static upsertCorrection = (
    variables: UpsertCorrectionVariables,
    addNotification: AddNotification,
    t: TFunction
  ): Promise<boolean> =>
    api.dispositions
      .upsertCorrection(variables)
      .then(res => {
        const payload = res.data?.upsertDispositionCorrection

        if (!payload?.success) {
          addNotification(
            NotifierType.ERROR,
            payload?.errors?.length
              ? payload.errors
              : t('agencies.dispositions.corrections.saveError')
          )
          return false
        }

        addNotification(NotifierType.SUCCESS, t('agencies.dispositions.corrections.saveSuccess'))
        return true
      })
      .catch(() => false)

  static sendCorrection = (
    correctionId: DispositionCorrection['id'],
    addNotification: AddNotification,
    t: TFunction
  ): Promise<boolean> =>
    api.dispositions
      .sendCorrection(correctionId)
      .then(res => {
        const payload = res.data?.sendDispositionCorrection

        if (!payload?.success) {
          addNotification(
            NotifierType.ERROR,
            payload?.errors?.length
              ? payload.errors
              : t('agencies.dispositions.corrections.sendError')
          )
          return false
        }

        addNotification(NotifierType.SUCCESS, t('agencies.dispositions.corrections.sendSuccess'))
        return true
      })
      .catch(() => false)

  /** Invoices delivered by the mailbox job are emailed right away, only a manual one waits. */
  static canAttachInvoice = (disposition: Disposition): boolean => !disposition.invoiceFileUrl

  static canSendInvoice = (disposition: Disposition): boolean =>
    disposition.invoiceAttachedManually && !disposition.invoiceSentAt

  /**
   * Only an unsent manual upload can be taken back - a mailbox or a sent invoice has reached the
   * client, and corrections are built on the invoice number.
   */
  static canRemoveInvoice = (disposition: Disposition): boolean =>
    disposition.invoiceAttachedManually &&
    !disposition.invoiceSentAt &&
    disposition.corrections.length === 0

  /** The invoice number is read from the file by the backend, nothing is saved on failure. */
  static attachInvoice = (
    dispositionId: Disposition['id'],
    file: File,
    addNotification: AddNotification,
    t: TFunction
  ): Promise<boolean> =>
    api.dispositions
      .attachInvoice(dispositionId, file)
      .then(res => {
        const payload = res.data?.attachDispositionInvoice

        if (!payload?.success) {
          addNotification(
            NotifierType.ERROR,
            payload?.errors?.length
              ? payload.errors
              : t('agencies.dispositions.invoices.attachError')
          )
          return false
        }

        addNotification(NotifierType.SUCCESS, t('agencies.dispositions.invoices.attachSuccess'))
        return true
      })
      .catch(() => false)

  static sendInvoice = (
    dispositionId: Disposition['id'],
    addNotification: AddNotification,
    t: TFunction
  ): Promise<boolean> =>
    api.dispositions
      .sendInvoice(dispositionId)
      .then(res => {
        const payload = res.data?.sendDispositionInvoice

        if (!payload?.success) {
          addNotification(
            NotifierType.ERROR,
            payload?.errors?.length ? payload.errors : t('agencies.dispositions.invoices.sendError')
          )
          return false
        }

        addNotification(NotifierType.SUCCESS, t('agencies.dispositions.invoices.sendSuccess'))
        return true
      })
      .catch(() => false)

  /**
   * Accounting issues the corrective invoice only for a sent correction. The mailbox job attaches
   * and emails it on its own; the upload is the fallback when the job could not match it.
   */
  static canAttachCorrectionInvoice = (correction: DispositionCorrection): boolean =>
    Boolean(correction.sentAt) && !correction.invoiceFileUrl

  static canSendCorrectionInvoice = (correction: DispositionCorrection): boolean =>
    correction.invoiceAttachedManually && !correction.invoiceSentAt

  /** Only an unsent manual upload can be taken back, the same as for the disposition invoice. */
  static canRemoveCorrectionInvoice = (correction: DispositionCorrection): boolean =>
    correction.invoiceAttachedManually && !correction.invoiceSentAt

  /** The corrective invoice number is read from the file by the backend, nothing is saved on failure. */
  static attachCorrectionInvoice = (
    correctionId: DispositionCorrection['id'],
    file: File,
    addNotification: AddNotification,
    t: TFunction
  ): Promise<boolean> =>
    api.dispositions
      .attachCorrectionInvoice(correctionId, file)
      .then(res => {
        const payload = res.data?.attachDispositionCorrectionInvoice

        if (!payload?.success) {
          addNotification(
            NotifierType.ERROR,
            payload?.errors?.length
              ? payload.errors
              : t('agencies.dispositions.correctionInvoices.attachError')
          )
          return false
        }

        addNotification(
          NotifierType.SUCCESS,
          t('agencies.dispositions.correctionInvoices.attachSuccess')
        )
        return true
      })
      .catch(() => false)

  static sendCorrectionInvoice = (
    correctionId: DispositionCorrection['id'],
    addNotification: AddNotification,
    t: TFunction
  ): Promise<boolean> =>
    api.dispositions
      .sendCorrectionInvoice(correctionId)
      .then(res => {
        const payload = res.data?.sendDispositionCorrectionInvoice

        if (!payload?.success) {
          addNotification(
            NotifierType.ERROR,
            payload?.errors?.length
              ? payload.errors
              : t('agencies.dispositions.correctionInvoices.sendError')
          )
          return false
        }

        addNotification(
          NotifierType.SUCCESS,
          t('agencies.dispositions.correctionInvoices.sendSuccess')
        )
        return true
      })
      .catch(() => false)

  static removeInvoice = (
    dispositionId: Disposition['id'],
    addNotification: AddNotification,
    t: TFunction
  ): Promise<boolean> =>
    api.dispositions
      .removeInvoice(dispositionId)
      .then(res => {
        const payload = res.data?.removeDispositionInvoice

        if (!payload?.success) {
          addNotification(
            NotifierType.ERROR,
            payload?.errors?.length
              ? payload.errors
              : t('agencies.dispositions.invoices.removeError')
          )
          return false
        }

        addNotification(NotifierType.SUCCESS, t('agencies.dispositions.invoices.removeSuccess'))
        return true
      })
      .catch(() => false)

  static removeCorrectionInvoice = (
    correctionId: DispositionCorrection['id'],
    addNotification: AddNotification,
    t: TFunction
  ): Promise<boolean> =>
    api.dispositions
      .removeCorrectionInvoice(correctionId)
      .then(res => {
        const payload = res.data?.removeDispositionCorrectionInvoice

        if (!payload?.success) {
          addNotification(
            NotifierType.ERROR,
            payload?.errors?.length
              ? payload.errors
              : t('agencies.dispositions.correctionInvoices.removeError')
          )
          return false
        }

        addNotification(
          NotifierType.SUCCESS,
          t('agencies.dispositions.correctionInvoices.removeSuccess')
        )
        return true
      })
      .catch(() => false)
}
